import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import {
  LayoutDashboard,
  FilePlus2,
  ListChecks,
  Building2,
  LogOut,
  Search,
  Settings,
  X,
} from "lucide-react";

export default function Sidebar({ open = false, onClose }) {
  const { user, logout } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();

  const NAV = [
    { to: "/", label: t("nav.dashboard"), icon: LayoutDashboard, testId: "nav-dashboard" },
    { to: "/complaints", label: t("nav.complaints"), icon: ListChecks, testId: "nav-complaints" },
    { to: "/complaints/new", label: t("nav.new"), icon: FilePlus2, testId: "nav-new" },
    { to: "/departments", label: t("nav.departments"), icon: Building2, testId: "nav-departments" },
    { to: "/search", label: t("nav.search"), icon: Search, testId: "nav-search" },
    { to: "/settings", label: t("nav.settings"), icon: Settings, testId: "nav-settings" },
  ];

  return (
    <aside
      data-testid="sidebar"
      className={`fixed left-0 top-0 z-40 h-screen w-64 border-r border-border bg-[hsl(var(--sidebar))] flex flex-col transition-transform duration-300 ease-out ${
        open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
      }`}
    >
      <div className="h-16 flex items-center gap-3 px-5 border-b border-border">
        <img
          src="https://customer-assets.emergentagent.com/job_complaint-lifecycle-1/artifacts/utuwjsdw_ChatGPT%20Image%20Jul%204%2C%202026%20at%2002_54_59%20PM.png"
          alt="Office of RSB emblem"
          loading="eager"
          decoding="async"
          fetchPriority="high"
          className="w-9 h-9 rounded-full object-cover ring-1 ring-border"
        />
        <div className="leading-tight flex-1 min-w-0">
          <div className="text-[13px] font-bold tracking-tight text-foreground truncate">
            {t("brand.title")}
          </div>
          <div className="text-[11px] text-muted-foreground font-mono-cms truncate">
            {t("brand.subtitle")}
          </div>
        </div>
        <button
          type="button"
          data-testid="sidebar-close"
          onClick={onClose}
          className="lg:hidden w-8 h-8 -mr-1 flex items-center justify-center rounded-md text-foreground/70 hover:bg-[hsl(var(--panel-alt))]"
          aria-label="Close menu"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        {NAV.map(({ to, label, icon: Icon, testId }) => (
          <NavLink
            key={to}
            to={to}
            end={to === "/"}
            data-testid={testId}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
                isActive
                  ? "bg-primary text-primary-foreground"
                  : "text-foreground/80 hover:bg-[hsl(var(--panel-alt))] hover:text-foreground"
              }`
            }
          >
            <Icon className="w-4 h-4" strokeWidth={2} />
            {label}
          </NavLink>
        ))}
      </nav>

      <div className="p-3 border-t border-border">
        <div className="px-3 py-2 mb-2">
          <div className="text-xs text-muted-foreground uppercase tracking-wider font-semibold">
            {t("auth.signed_in")}
          </div>
          <div className="text-sm font-semibold text-foreground truncate mt-0.5">
            {user?.name}
          </div>
          <div className="text-xs text-muted-foreground truncate">
            {user?.email} · <span className="uppercase">{user?.role}</span>
          </div>
        </div>
        <button
          data-testid="logout-btn"
          onClick={async () => {
            await logout();
            navigate("/login");
          }}
          className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm font-semibold text-foreground/80 hover:bg-[hsl(var(--panel-alt))] hover:text-foreground transition-colors"
        >
          <LogOut className="w-4 h-4" />
          {t("auth.logout")}
        </button>
      </div>
    </aside>
  );
}
